import { Link } from 'react-router-dom';
import { Pill, SurfacePagePanel, Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { cn } from '@/shared/cn';
import { formatDayMonth } from '@/versions/v1/shared/clock.derive';
import {
  STAGE_INK_LINK,
  STAGE_ROWS,
  STAGE_TABLE,
  STAGE_TABLE_SCROLL,
  STAGE_TD,
  STAGE_TD_FIRST,
  STAGE_TH,
  STAGE_TH_FIRST,
  STAGE_THEAD,
} from './stage-body-ui';
import {
  investigationRunStatusLabel,
  runStepCount,
  type InvestigationRunRow,
} from '@/versions/v1/modules/advisory/InvestigationRun.transform';

function statusTone(status: InvestigationRunRow['status']): 'success' | 'warning' | 'default' {
  if (status === 'approved') return 'success';
  if (status === 'running') return 'warning';
  return 'default';
}

/**
 * K15 / D24: the flow landing's Diagnosis stage lists the Excursion
 * investigation workflow's runs. Every row opens its trace on the case page's
 * Investigation column (`?run=`), so the stage reads as the workflow's queue
 * rather than a second case list.
 */
export function InvestigationRunsPanel({
  runs,
  projectId,
  className,
  layout = 'table',
}: {
  runs: readonly InvestigationRunRow[];
  projectId: string;
  className?: string;
  /**
   * `table` (default) spans a full row; `rows` is the Diagnosis 4 beside the
   * candidates 8 (polish.P28b): run over case, started · steps, status and
   * trace link on the right, hairline rows.
   */
  layout?: 'table' | 'rows';
}) {
  if (layout === 'rows') {
    return (
      <SurfacePagePanel title="Investigation runs" meta="Excursion investigation" className={className} flush>
        <ul data-stage-body-list="investigation-runs" className={cn(STAGE_TABLE_SCROLL, STAGE_ROWS)}>
          {runs.map((run) => (
            <li key={run.id} data-investigation-run-row={run.id} className="flex min-w-0 items-center justify-between gap-3 px-4 py-2.5">
              <div className="min-w-0">
                <p className="font-mono text-xs font-semibold text-ink-primary">
                  {run.id} <span className="font-normal text-ink-secondary">· {run.caseCode}</span>
                </p>
                <p className="mt-0.5 font-mono text-[10px] tabular-nums text-muted">
                  {formatDayMonth(run.startedAt)} {run.startedAt.slice(11, 16)} · {runStepCount(run.steps)}
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <Pill tone={statusTone(run.status)} className="px-1.5 text-[10px]">
                  {investigationRunStatusLabel(run.status)}
                </Pill>
                <Link
                  to={`/projects/${projectId}/advisory/case/${run.caseId}?run=${run.id}`}
                  className={STAGE_INK_LINK}
                  data-investigation-run-link={run.id}
                >
                  open trace
                </Link>
              </div>
            </li>
          ))}
        </ul>
      </SurfacePagePanel>
    );
  }
  return (
    <SurfacePagePanel
      title="Investigation runs"
      meta="Excursion investigation"
      className={className}
      flush
    >
      <div className={STAGE_TABLE_SCROLL}>
        <Table wrap={false} className={STAGE_TABLE}>
          <THead className={STAGE_THEAD}>
            <TR>
              <TH className={STAGE_TH_FIRST}>Run</TH>
              <TH className={STAGE_TH}>Case</TH>
              <TH className={STAGE_TH}>Started</TH>
              <TH className={STAGE_TH}>Status</TH>
              <TH className={cn(STAGE_TH, 'text-right')}>Steps</TH>
              <TH className={cn(STAGE_TH, 'pr-4 text-right')}>Trace</TH>
            </TR>
          </THead>
          <TBody data-stage-body-list="investigation-runs">
            {runs.map((run) => (
              <TR key={run.id} data-investigation-run-row={run.id}>
                <TD className={cn(STAGE_TD_FIRST, 'font-mono text-xs font-semibold text-ink-primary')}>{run.id}</TD>
                <TD className={cn(STAGE_TD, 'font-mono text-xs text-ink-secondary')}>{run.caseCode}</TD>
                <TD className={cn(STAGE_TD, 'font-mono text-xs tabular-nums text-muted')}>
                  {formatDayMonth(run.startedAt)} {run.startedAt.slice(11, 16)}
                </TD>
                <TD className={STAGE_TD}>
                  <Pill tone={statusTone(run.status)} className="px-1.5 text-[10px]">
                    {investigationRunStatusLabel(run.status)}
                  </Pill>
                </TD>
                <TD className={cn(STAGE_TD, 'text-right text-xs tabular-nums text-ink-secondary')}>{runStepCount(run.steps)}</TD>
                <TD className={cn(STAGE_TD, 'pr-4 text-right')}>
                  <Link
                    to={`/projects/${projectId}/advisory/case/${run.caseId}?run=${run.id}`}
                    className={STAGE_INK_LINK}
                    data-investigation-run-link={run.id}
                  >
                    open trace
                  </Link>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </SurfacePagePanel>
  );
}
